import { isNativePlatform } from '@/lib/platform';

/** CSS custom property written to document.documentElement by useKeyboardInset. */
export const KEYBOARD_HEIGHT_CSS_VAR = '--keyboard-height';

/** Data attribute on document.documentElement when keyboard inset > 0. */
export const KEYBOARD_OPEN_ATTR = 'data-keyboard-open';

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]';

/**
 * Compute keyboard overlap height from the Visual Viewport API (mobile web).
 * Returns 0 when visualViewport is unavailable or keyboard is not open.
 */
export function computeVisualViewportKeyboardInset(): number {
  if (typeof window === 'undefined') return 0;

  const vv = window.visualViewport;
  if (!vv) return 0;

  const layoutHeight = window.innerHeight;
  const visibleBottom = vv.height + vv.offsetTop;
  const inset = Math.max(0, Math.round(layoutHeight - visibleBottom));

  // Ignore tiny deltas from browser chrome / rounding.
  return inset > 50 ? inset : 0;
}

/** Apply keyboard inset CSS variables and open-state flag on the document root. */
export function applyKeyboardInset(heightPx: number): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.style.setProperty(KEYBOARD_HEIGHT_CSS_VAR, `${heightPx}px`);

  if (heightPx > 0) {
    root.setAttribute(KEYBOARD_OPEN_ATTR, 'true');
  } else {
    root.removeAttribute(KEYBOARD_OPEN_ATTR);
  }
}

/** Reset keyboard inset state (e.g. on unmount). */
export function resetKeyboardInset(): void {
  applyKeyboardInset(0);
}

/** Returns true when the element is an editable control that can summon the keyboard. */
export function isEditableElement(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.matches(FOCUSABLE_SELECTOR)) return true;
  return el.isContentEditable;
}

/** Blur the currently focused element if it is editable (dismisses software keyboard). */
export function blurActiveEditableElement(): void {
  if (typeof document === 'undefined') return;
  const active = document.activeElement;
  if (isEditableElement(active)) {
    active.blur();
  }
}

export interface ScrollFocusedIntoViewOptions {
  /** Extra padding below the focused element (px). */
  padding?: number;
  /** Scroll behavior. */
  behavior?: ScrollBehavior;
  /** Override keyboard height; defaults to CSS var or visualViewport. */
  keyboardHeight?: number;
}

/**
 * Scroll a focused editable element into the visible area above the keyboard.
 * Works inside scroll containers and page-level scroll.
 */
export function scrollFocusedElementIntoView(
  container: HTMLElement | null | undefined,
  options: ScrollFocusedIntoViewOptions = {},
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const active = document.activeElement;
  if (!isEditableElement(active)) return;

  const padding = options.padding ?? 16;
  const native = isNativePlatform();
  const behavior = options.behavior ?? (native ? 'auto' : 'smooth');

  let keyboardHeight = options.keyboardHeight;
  if (keyboardHeight === undefined) {
    const cssVal = document.documentElement.style.getPropertyValue(KEYBOARD_HEIGHT_CSS_VAR);
    const parsed = parseInt(cssVal, 10);
    keyboardHeight = Number.isFinite(parsed) && parsed > 0
      ? parsed
      : computeVisualViewportKeyboardInset();
  }

  const visibleBottom =
    (window.visualViewport?.height ?? window.innerHeight) - keyboardHeight;
  const rect = active.getBoundingClientRect();

  if (rect.bottom <= visibleBottom - padding && rect.top >= padding) {
    return;
  }

  // iOS WKWebView: scrolling a full-screen overlay while the keyboard animates
  // dismisses the keyboard. Only nudge the focused field — never scroll the backdrop.
  if (native) {
    active.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    return;
  }

  // Prefer scrolling within the nearest scrollable ancestor inside the container.
  if (container?.contains(active)) {
    const elRect = active.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offsetInContainer = elRect.top - containerRect.top + container.scrollTop;
    const targetScrollTop =
      offsetInContainer - container.clientHeight / 2 + elRect.height / 2 + keyboardHeight / 2;

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior,
    });
    return;
  }

  active.scrollIntoView({
    block: 'center',
    behavior,
  });
}

/** Keyboard height from the CSS var (native and web), falling back to the visual viewport. */
function readKeyboardHeight(): number {
  const parsed = parseInt(
    document.documentElement.style.getPropertyValue(KEYBOARD_HEIGHT_CSS_VAR),
    10,
  );
  return Number.isFinite(parsed) && parsed > 0 ? parsed : computeVisualViewportKeyboardInset();
}

/** Nearest ancestor that scrolls vertically, or null when the window scrolls. */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Page-level variant of scrollFocusedElementIntoView: scroll the focused field inside `root`
 * so it sits above the keyboard. The keyboard overlays the page (native resize: None, and
 * mobile Safari), so `scrollIntoView({ block: 'nearest' })` sees the field as visible and
 * does nothing; this measures the area left above the keyboard and scrolls by the overlap.
 */
export function scrollFocusedAboveKeyboard(
  root: HTMLElement | null,
  { padding = 24, behavior = 'smooth' }: { padding?: number; behavior?: ScrollBehavior } = {},
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const active = document.activeElement;
  if (!isEditableElement(active) || (root && !root.contains(active))) return;

  const vv = window.visualViewport;
  const visibleTop = (vv?.offsetTop ?? 0) + padding;
  const visibleBottom =
    (isNativePlatform() || !vv
      ? window.innerHeight - readKeyboardHeight()
      : vv.offsetTop + vv.height) - padding;

  const rect = active.getBoundingClientRect();
  let delta = 0;
  if (rect.bottom > visibleBottom) {
    // A field taller than the space left keeps its top in view.
    delta = Math.min(rect.bottom - visibleBottom, rect.top - visibleTop);
  } else if (rect.top < visibleTop) {
    delta = rect.top - visibleTop;
  }
  if (Math.abs(delta) < 1) return;

  // Absolute target, not scrollBy: a repeat call during a smooth scroll measures the field
  // mid-animation, and current scrollTop + delta still lands on the same spot instead of
  // stacking another offset.
  const scroller = findScrollParent(active);
  if (scroller) {
    scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + delta), behavior });
  } else {
    window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior });
  }
}
